import React, { useCallback } from 'react'
import { SearchProvider } from 'modules/react/search/SearchContext'
import axios from 'axios'
import { useFirstRender } from 'modules/react/hooks/useFirstRender'
import { debounce, isEmpty } from 'lodash'

export default function SearchContainer ({
  children,
  searchUrl,
  nonce = '',
  initialSearch = '',
  initialPage = 1,
  response = {},
  initiallySelectedFilters = [],
}) {
  const { data: { data, filters, current_page, last_page, total } } = response

  const [results, setResults] = React.useState(data || [])
  const [totalResults, setTotalResults] = React.useState(total || 0)
  const [existingFilters, setExistingFilters] = React.useState(filters || [])
  const [pageCount, setPageCount] = React.useState(last_page || 1)

  const [containerRef, setContainerRef] = React.useState(null)
  const [currentSearch, setCurrentSearch] = React.useState(initialSearch)
  const [selectedFilters, setSelectedFilters] = React.useState(initiallySelectedFilters)
  const [page, setPage] = React.useState(current_page || 1)

  const debouncedSearch = useCallback(debounce((forPage) =>
    doSearch(forPage)
  ))

  const doSearch = (forPage) => {
    return axios.get(searchUrl, {
      params: {
        ...getSearchParams(),
        side: forPage?.toString() || page?.toString(),
      },
    }, {
      headers: {
        'X-WP-Nonce': nonce,
      }
    }).then((response) => {
      updateStateFromResponse(response)
      updateHistory()
      //unselectUnavailableFilters()
      return response
    }).catch((error) => {
      return Promise.reject(error)
    })
  }

  const updateStateFromResponse = (response) => {
    const { data: { data, filters, current_page, last_page, total } } = response
    setResults(data)
    setExistingFilters(filters)
    setPageCount(last_page)
    setPage(current_page)
    setTotalResults(total)
  }

  const firstRender = useFirstRender()

  const scrollMemory = () => {
    const scrollOffset =  localStorage.getItem('scrollOffset')
    window.scrollTo({ top: scrollOffset ?? 0 })
    window.addEventListener('scroll', debounce(() => {
      localStorage.setItem('scrollOffset', window.scrollY.toString())
    }, 300))
  }

  React.useEffect(() => {
    scrollMemory()
    updateStateFromResponse(response)
  }, [])

  React.useEffect(() => {
    if (firstRender) {
      return
    }

    updateHistory()

    debouncedSearch(1)
  }, [selectedFilters, currentSearch])

  const updateHistory = () => {
    const url = new URL(window.location.href.split('?')[0])

    url.search = new URLSearchParams(getSearchParams())

    window.history.pushState('', '', decodeURIComponent(url.toString()))
  }

  const getSearchParams = () => {
    return Object.fromEntries(Object.entries({
      s: currentSearch,
      former: selectedFilters.former?.filter(([_, v]) => !isEmpty(v)) || null,
      kategorier: selectedFilters.kategorier?.filter(([_, v]) => !isEmpty(v)) || null,
      typer: selectedFilters.typer?.filter(([_, v]) => !isEmpty(v)) || null,
      forloeb: selectedFilters.forloeb || null,
      malgruppe: selectedFilters.malgruppe?.filter(([_, v]) => !isEmpty(v)) || null,
      side: page?.toString(),
    }).filter(([_, v]) => !isEmpty(v)))
  }

  React.useEffect(() => {
    if (firstRender) {
      return
    }

    updateHistory()
    doSearch().then(() => {
      if(containerRef?.current) {
        const headerOffset = 120;
        const elementPosition = containerRef.current.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth"
        });
      }
    })
  }, [page])

  const contextObject = {
    results: results,
    totalResults: totalResults,
    filters: existingFilters,
    search: currentSearch,
    setSearch: setCurrentSearch,
    selectedFilters: selectedFilters,
    goToPage: setPage,
    previousPage: () => setPage(page - 1),
    nextPage: () => setPage(page + 1),
    page: page,
    pageCount: pageCount,
    isFilterSelected: (type, filter) => {
      const filterForType = selectedFilters[type]

      if(!filterForType) {
        return false
      }

      if(Array.isArray(filterForType)) {
        return filterForType?.indexOf(filter) > -1
      }

      return filterForType === filter
    },
    selectFilter: (type, filter, single = false) => {
      let newSelectedFilters = Object.assign({}, selectedFilters)

      if(single) {
        newSelectedFilters[type] = filter
      }
      else {
        if (isEmpty(newSelectedFilters[type])) {
          newSelectedFilters[type] = [filter]
        } else {
          newSelectedFilters[type].push(filter)
        }
      }

      setSelectedFilters(newSelectedFilters)
    },
    unselectFilter: (type, filter) => {
      let newSelectedFilters = selectedFilters[type] || []

      if(!Array.isArray(selectedFilters[type])) {
        const newFilters = {
          ...selectedFilters,
        }

        delete newFilters[type]

        return setSelectedFilters(newFilters)
      }

      const indexOfFilter = newSelectedFilters.indexOf(filter)

      if (indexOfFilter < 0) {
        return
      }

      newSelectedFilters.splice(indexOfFilter, 1)

      setSelectedFilters({
        ...selectedFilters,
        [type]: newSelectedFilters
      })
    },
    unselectAllFilters: () => {
      setSelectedFilters([])
      const checkboxes = document.querySelectorAll('.search-page__filter-item input[type="checkbox"]')
      if (checkboxes.length > 0) {
        checkboxes.forEach(checkbox => {
          checkbox.checked = false
        })
      }
    },
    setContainerRef: (ref) => {
      setContainerRef(ref)
    }
  }

  return (
    <div className="search-page br-tl">
      <SearchProvider value={contextObject}>
        {children}
      </SearchProvider>
    </div>
  )
}